import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Star,
  MapPin,
  Phone,
  ArrowLeft,
  Send,
  Clock,
  Loader2,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { normalizeShop, normalizeOffer, normalizeReview } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function ShopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [offers, setOffers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);

  const initialTab =
    searchParams.get("tab") === "offers" || searchParams.get("tab") === "reviews"
      ? searchParams.get("tab")
      : "details";
  const [tab, setTab] = useState(initialTab);
  const tabsRef = useRef(null);

  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [msgText, setMsgText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, o, r] = await Promise.all([
        api.get(`/shops/${id}`),
        api.get(`/offers`, { params: { shop_id: id } }),
        api.get(`/shops/${id}/reviews`),
      ]);
      setShop(normalizeShop(s.data));
      setOffers((o.data || []).map(normalizeOffer));
      setReviews((r.data?.reviews || []).map(normalizeReview));
    } catch {
      toast.error("Could not load this shop");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-scroll to the tabs area if we deep-linked with ?tab=offers/reviews
    if (!loading && tab !== "details" && tabsRef.current) {
      tabsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, tab]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const requireAuth = () => {
    if (!user) {
      toast.info("Please log in to continue");
      navigate("/auth");
      return false;
    }
    return true;
  };

  const askAboutOffer = async (offer) => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await api.post("/messages", {
        shop_id: id,
        text: `Hi! I'm interested in your offer: "${offer.title}" (${offer.discount}). Is it available?`,
      });
      toast.success("Sent to the shop", { description: "They'll reply in your chats." });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitReview = async () => {
    if (!requireAuth()) return;
    if (!reviewText.trim()) return toast.error("Write a short review first");
    setBusy(true);
    try {
      await api.post(`/shops/${id}/reviews`, { rating: reviewRating, comment: reviewText });
      setReviewText("");
      toast.success("Thanks for your review!");
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async () => {
    if (!requireAuth()) return;
    if (!msgText.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { shop_id: id, text: msgText });
      setMsgText("");
      toast.success("Message sent to the shop");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-secondary pt-[72px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );

  if (!shop)
    return (
      <div className="min-h-screen grid place-items-center bg-secondary pt-[72px]">
        <div className="text-center">
          <p className="font-display font-bold text-2xl">Shop not found</p>
          <Link to="/browse" className="text-primary font-semibold mt-2 inline-block">
            Back to browse
          </Link>
        </div>
      </div>
    );

  const images = shop.images?.length ? shop.images : [];

  return (
    <div className="min-h-screen bg-background pt-[72px]">
      {/* Gallery hero */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-8">
        <button
          data-testid="shop-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
          <motion.div
            key={activeImg}
            initial={{ opacity: 0.4, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden h-[300px] sm:h-[440px] grain"
          >
            <img src={images[activeImg]} alt={shop.name} className="w-full h-full object-cover" />
            <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-wider bg-background/90 px-3 py-1.5 rounded-full capitalize">
              {shop.category}
            </span>
          </motion.div>
          <div className="grid grid-cols-3 lg:grid-cols-2 gap-4 content-start">
            {images.map((img, i) => (
              <button
                key={i}
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`rounded-2xl overflow-hidden h-[100px] lg:h-[210px] border-2 transition-colors ${
                  activeImg === i ? "border-primary" : "border-transparent"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Header + content */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10 grid lg:grid-cols-[1.6fr_1fr] gap-10">
        <div>
          <div className="flex flex-wrap items-center gap-4 mb-3">
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter" data-testid="shop-name">
              {shop.name}
            </h1>
            <span className="flex items-center gap-1.5 bg-primary/10 text-primary rounded-full px-3 py-1.5 font-bold">
              <Star className="w-4 h-4 fill-primary" /> {shop.rating}
              <span className="text-muted-foreground font-medium">({shop.review_count})</span>
            </span>
          </div>
          <p className="flex items-center gap-2 text-muted-foreground mb-8">
            <MapPin className="w-4 h-4" /> {shop.address}
          </p>

          <Tabs ref={tabsRef} value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-secondary rounded-full p-1 mb-8">
              <TabsTrigger value="details" data-testid="tab-details" className="rounded-full px-6">
                Details
              </TabsTrigger>
              <TabsTrigger value="offers" data-testid="tab-offers" className="rounded-full px-6">
                Offers ({offers.length})
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-full px-6">
                Reviews ({reviews.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details">
              <p className="text-lg leading-relaxed text-foreground/90 max-w-2xl">
                {shop.description}
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
                <InfoTile icon={Clock} label="Hours" value="Open · 9:00 AM – 9:00 PM" />
                <InfoTile icon={Phone} label="Phone" value={shop.phone} />
              </div>
            </TabsContent>

            <TabsContent value="offers">
              {offers.length === 0 ? (
                <p className="text-muted-foreground">No active offers right now. Check back soon!</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="offers-list">
                  {offers.map((o) => (
                    <div
                      key={o.id}
                      className="relative bg-card rounded-3xl border border-border overflow-hidden group"
                    >
                      <div className="h-28 overflow-hidden">
                        <img src={o.image} alt="" className="w-full h-full object-cover" />
                        <span className="absolute top-3 right-3 bg-primary text-primary-foreground font-bold text-sm px-3 py-1.5 rounded-full">
                          {o.discount}
                        </span>
                      </div>
                      <div className="p-5">
                        <h3 className="font-display font-bold text-lg tracking-tight">{o.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{o.description}</p>
                        <button
                          data-testid={`ask-offer-${o.id}`}
                          disabled={busy}
                          onClick={() => askAboutOffer(o)}
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-2.5 hover:bg-primary/90 transition-colors disabled:opacity-60"
                        >
                          <Send className="w-4 h-4" /> Ask the shop
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="reviews">
              <div className="mb-8 bg-secondary rounded-3xl p-6 border border-border">
                <h3 className="font-display font-bold text-lg mb-4">Write a review</h3>
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      data-testid={`review-star-${n}`}
                      onClick={() => setReviewRating(n)}
                    >
                      <Star
                        className={`w-7 h-7 transition-colors ${
                          n <= reviewRating ? "fill-primary text-primary" : "text-border"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <Textarea
                  data-testid="review-text"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Share your experience…"
                  className="bg-card border-border rounded-2xl"
                />
                <button
                  data-testid="submit-review"
                  disabled={busy}
                  onClick={submitReview}
                  className="mt-4 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-2.5 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  Post review
                </button>
              </div>

              <div className="space-y-4" data-testid="reviews-list">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-card rounded-3xl border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold uppercase">
                          {r.user_name?.[0]}
                        </span>
                        <span className="font-bold">{r.user_name}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[...Array(r.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                        ))}
                      </div>
                    </div>
                    <p className="mt-3 text-foreground/90 leading-relaxed">{r.comment}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sticky contact card */}
        <div>
          <div className="sticky top-24 bg-card rounded-3xl border border-border p-6 shadow-[0_20px_50px_-30px_rgba(42,36,30,0.4)]">
            <h3 className="font-display font-bold text-xl tracking-tight mb-1">Contact this shop</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Ask about stock, hours, or bookings — the owner gets it directly.
            </p>
            <a
              href={`tel:${shop.phone}`}
              data-testid="shop-call"
              className="flex items-center gap-3 rounded-2xl bg-secondary p-4 mb-3 hover:bg-accent transition-colors"
            >
              <Phone className="w-5 h-5 text-primary" />
              <span className="font-semibold">{shop.phone}</span>
            </a>

            <Dialog>
              <DialogTrigger asChild>
                <button
                  data-testid="open-message"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-primary/90 transition-colors"
                >
                  <Send className="w-4 h-4" /> Message shop
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display">Message {shop.name}</DialogTitle>
                  <DialogDescription>Ask about stock, hours or bookings. The shop replies in your chats.</DialogDescription>
                </DialogHeader>
                <Textarea
                  data-testid="message-text"
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  placeholder="Hi! I'd like to ask about…"
                  className="rounded-2xl"
                />
                <button
                  data-testid="send-message"
                  disabled={busy}
                  onClick={sendMessage}
                  className="rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  Send message
                </button>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 bg-secondary rounded-2xl p-4 border border-border">
      <span className="grid place-items-center w-10 h-10 rounded-xl bg-card text-primary">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
        <p className="font-semibold">{value}</p>
      </div>
    </div>
  );
}
